import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { LandingService } from '../../services/landing.service';
import { IProduct } from '../../../shared/interfaces/IProduct';
import { Subject } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-subscription-product-page',
  templateUrl: './subscription-product-page.component.html',
  styleUrls: ['./subscription-product-page.component.scss'],
  animations: [
    trigger('carouselSlide', [
      transition(':increment', [
        style({ opacity: 0, transform: 'translateX(40px)' }),
        animate('300ms ease', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
      transition(':decrement', [
        style({ opacity: 0, transform: 'translateX(-40px)' }),
        animate('300ms ease', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
    ]),
  ]
})
export class SubscriptionProductPageComponent implements OnInit, OnDestroy {
  products: IProduct[] = [];
  viewProducts: IProduct[] = [];
  productsForSubs: any[] = [];
  infiniteProducts: any[] = []; // Array infinito para el carrusel
  lastProductIndex = 0;
  firstProductIndex = 0;
  itemsPerPage = 4;
  pageIndex = 0; // usado solo para disparar animaciones
  isMobile = false;
  destroy$: Subject<any>;
  zip_code: any;
  loading = false;
  constructor(
    private landingService: LandingService,
    private router: Router,
    private _sanitizer: DomSanitizer,
    private route: ActivatedRoute
  ) {
    this.destroy$ = new Subject<any>();
  }
  isDateInRange: boolean = false;
  ngOnInit(): void {
    // detectar dispositivo
    this.isMobile = window.innerWidth <= 767;
    // CYBERFIL 2024
    const today = new Date(); // Fecha actual
    const startDate = new Date('2024-11-01T00:00:00-03:00'); // Fecha de inicio
    const endDate = new Date(2024, 10, 15, 23, 59, 59, 999); // 15 de noviembre

    // Asegúrate de que las comparaciones se hagan en la misma zona horaria
    this.isDateInRange = today >= startDate && today <= endDate;

    // Para depurar
    console.log('Fecha hoy:', today);
    console.log('Fecha de inicio:', startDate);
    console.log('Fecha de fin:', endDate);
    console.log('¿Está en el rango?', this.isDateInRange);
    // END CYBERFIL 2024

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (params.zipCode) {
          this.zip_code = params.zipCode;
        }
        if (params.disc) {
          if (params.disc == 'CYBERFIL50' || params.disc == 'CYBERFIL20') {
            this.discount = params.disc;
          }
        }
        this.products = [];
        this.loading = true;
        this.landingService
          .getProducts(this.zip_code)
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {
            this.products = res.map((item) => {
              item.imageUrl = item.galeries?.[0]?.file_name;

              return item;
            });
            // Determinar items por página (3 en desktop para destacar el del medio)
            this.itemsPerPage = this.isMobile ? 4 : 3;

            // Filtrar productos con suscripción mensual
            this.productsForSubs = [];
            this.products.forEach((product) => {
              if (product.periodicity && product.periodicity.length > 0) {
                const hasMensual = product.periodicity.find((p) => p.months === 1);
                if (hasMensual) {
                  this.productsForSubs.push(product);
                }
              }
            });

            // Inicializar índices del carrusel
            this.firstProductIndex = 0;
            this.lastProductIndex = Math.min(this.itemsPerPage, this.productsForSubs.length);
            this.updateViewProducts();
            this.loading = false;
            this.gotoTop();
          });
      });
  }
  @HostListener('window:resize')
  onResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 767;
    // Recalcular configuración cuando cambia el breakpoint
    if (wasMobile !== this.isMobile) {
      this.itemsPerPage = this.isMobile ? 4 : 3; // 3 productos en desktop
      // Reiniciar desde el inicio
      this.firstProductIndex = 0;
      this.lastProductIndex = Math.min(this.itemsPerPage, this.productsForSubs.length);
      this.pageIndex = 0;
      this.updateViewProducts();
    }
  }
  private updateViewProducts(): void {
    if (this.isMobile) {
      // En mobile mostrar todos los productos originales
      this.viewProducts = this.productsForSubs.slice();
    } else {
      // En desktop no necesitamos updateViewProducts porque usamos infiniteProducts directamente
      // Este método se mantiene para compatibilidad pero infiniteProducts se usa en el template
      this.viewProducts = this.productsForSubs.slice();
    }
  }

  private createInfiniteProductArray(): void {
    if (this.productsForSubs.length === 0) {
      this.infiniteProducts = [];
      return;
    }
    
    // Crear un array con múltiples copias para navegación infinita
    // Repetir los productos al menos 5 veces para tener suficiente buffer
    this.infiniteProducts = [];
    for (let i = 0; i < 5; i++) {
      this.infiniteProducts = [...this.infiniteProducts, ...this.productsForSubs];
    }
  }

  nextProducts(): void {
    if (this.isMobile) return; // sin carrusel en mobile
    const originalLength = this.productsForSubs.length;
    if (originalLength === 0) return;
    
    // Avanza de a un producto
    this.firstProductIndex++;
    
    // Si llegamos al final de los productos originales, volver al inicio
    if (this.firstProductIndex >= originalLength) {
      this.firstProductIndex = 0;
    }
    
    this.pageIndex++;
  }

  prevProducts(): void {
    if (this.isMobile) return; // sin carrusel en mobile
    const originalLength = this.productsForSubs.length;
    if (originalLength === 0) return;
    
    // Retrocede de a un producto
    this.firstProductIndex--;
    
    // Si llegamos antes del inicio, ir al final
    if (this.firstProductIndex < 0) {
      this.firstProductIndex = originalLength - 1;
    }
    
    this.pageIndex--;
  }

  get showCarousel(): boolean {
    return !this.isMobile && this.productsForSubs.length > 1; // Mostrar carrusel si hay más de 1 producto
  }

  isProductVisible(index: number): boolean {
    if (this.isMobile) {
      return true; // En mobile todos son visibles
    }
    
    // En desktop, solo mostrar 3 productos
    const totalProducts = this.productsForSubs.length;
    const visibleIndices = [];
    
    for (let i = 0; i < 3; i++) {
      const visibleIndex = (this.firstProductIndex + i) % totalProducts;
      visibleIndices.push(visibleIndex);
    }
    
    return visibleIndices.includes(index);
  }

  getVisibleProducts(): any[] {
    if (this.isMobile || this.productsForSubs.length === 0) {
      return this.productsForSubs; // En mobile mostrar todos
    }
    
    // En desktop, retornar exactamente 3 productos
    const totalProducts = this.productsForSubs.length;
    const visibleProducts = [];
    
    for (let i = 0; i < 3; i++) {
      const productIndex = (this.firstProductIndex + i) % totalProducts;
      visibleProducts.push(this.productsForSubs[productIndex]);
    }
    
    return visibleProducts;
  }
  formatNumber(numeroOriginal: string): string {
    // Convierte el número de cadena a número de punto flotante (float)
    const numero = parseFloat(numeroOriginal);

    // Aplica el formato con tres decimales y usa '.' como separador de miles
    const numeroFormateado = numero.toLocaleString('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return numeroFormateado;
  }
  gotoTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  discount = '';
  getPrice(product: IProduct): {
    original_price: number;
    price: number;
    periodicity: string;
    original_cost: number;
    cost: number;
    periodicity_label: string;
    percentage: number;
  } | null {
    if (!product.periodicity || product.periodicity.length === 0) {
      return null;
    }

    const monthly = product.periodicity.find((item) => item.months >= 1);
    const discount = this.discount;

    if (monthly) {
      let price = monthly.pivot.periodic_price; // Precio original
      let cost;
      let periodicity_label;
      let percentage;
      // Aplicar descuento si corresponde
      if (discount === 'CYBERFIL50') {
        price = price * 0.5; // Aplicar 50% de descuento
        cost = 0;
        periodicity_label = 'en tu primer mes';
        percentage = 50;
      } else if (discount === 'CYBERFIL20') {
        price = price * 0.8;
        cost = 0;
        periodicity_label = 'en tus primeros 3 meses';
        percentage = 20;
      }

      if (monthly.months === 1) {
        return {
          original_price: monthly.pivot.periodic_price,
          price: price,
          cost: cost,
          original_cost: monthly.pivot.price,
          periodicity: ' x mes',
          periodicity_label: periodicity_label,
          percentage: percentage,
        };
      } else {
        switch (monthly.months) {
          case 2: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: 'por bimestre',
              periodicity_label: '',
              percentage: percentage,
            };
          }
          case 3: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: 'por trimestre',
              periodicity_label: '',
              percentage: percentage,
            };
          }
          case 4: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: 'por cuatrimestre',
              periodicity_label: '',
              percentage: percentage,
            };
          }
          case 6: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: 'por semestre',
              periodicity_label: '',
              percentage: percentage,
            };
          }
          default: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: `por ${monthly.months} meses`,
              periodicity_label: '',
              percentage: percentage,
            };
          }
        }
      }
    }

    const sale = product.periodicity.find(
      (item) => item.months === 0 && item.years === 0 && item.days === 0
    );
    if (sale) {
      let price = sale.pivot.periodic_price; // Precio original para la venta única
      let cost;
      // Aplicar descuento si corresponde
      if (discount === 'CYBERFIL50') {
        price = price * 0.5; // Aplicar 50% de descuento
        cost = 0;
      } else if (discount === 'CYBERFIL20') {
        price = price * 0.8;
        cost = 0;
      }

      return {
        original_price: sale.pivot.periodic_price,
        price: price,
        original_cost: sale.pivot.price,
        cost: 0,
        periodicity: `por única vez`,
        periodicity_label: '',
        percentage: 0,
      };
    }

    return null; // Si no se encuentra ninguna periodicidad
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  productDetail(product: IProduct, type: 'sale' | 'sub' = 'sub', ): void {
    this.loading = true;
    this.landingService.setProducts(this.productsForSubs);
    let params = '?';
    if (this.zip_code) {
      params = 'zipCode=' + this.zip_code;
    }
    if (this.discount) {  
      params += '&disc=' + this.discount;
    }
    // Add the purchase type parameter
    params += `&type=${type}`;
    this.router.navigateByUrl(`/products/${product.id}${params}`);
  }
}
